/**
 * =============================================================================
 * PHS TOP BAR - Enterprise Header
 * =============================================================================
 * Premium top bar with search, quick actions, notifications, user profile.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Plus, Bell, Settings, LogOut, ChevronDown,
  User, Menu, Wifi, WifiOff, RefreshCw, Clock, AlertTriangle,
  FileText, DollarSign, CheckCircle, X, Check, Command
} from 'lucide-react';
import { TOKENS } from './PHSCommandLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Category icons and colors for notifications
const CATEGORY_CONFIG = {
  sla: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' },
  quote: { icon: FileText, color: 'text-purple-500', bg: 'bg-purple-50' },
  completion: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
  finance: { icon: DollarSign, color: 'text-blue-500', bg: 'bg-blue-50' },
  system: { icon: Bell, color: 'text-slate-500', bg: 'bg-slate-50' }
};

// Priority styling
const PRIORITY_STYLES = {
  critical: 'border-l-4 border-l-red-500',
  high: 'border-l-4 border-l-orange-500',
  medium: 'border-l-4 border-l-yellow-500',
  low: 'border-l-4 border-l-slate-300'
};

// Format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export default function PHSTopBar({ 
  user,
  onLogout,
  onMenuClick,
  isConnected = true,
  onOpenCommandBar
}) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const resp = await fetch(`${API_URL}/api/notifications?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Poll every minute
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Fetch when dropdown opens
  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications, fetchNotifications]);

  // Close menus on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.job_id) {
      setShowNotifications(false);
      navigate(`/phs/helpdesk/job/${notification.job_id}`);
    }
  };

  return (
    <header 
      className="sticky top-0 z-30 border-b"
      style={{ 
        backgroundColor: TOKENS.colors.cardBg,
        borderColor: TOKENS.colors.border 
      }}
    >
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Left: Menu button (mobile) + Search */}
        <div className="flex items-center gap-4 flex-1">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 transition"
            data-testid="topbar-menu-btn"
          >
            <Menu className="w-5 h-5 text-slate-600" />
          </button>

          {/* Command Bar Trigger - replaces simple search */}
          <button 
            onClick={onOpenCommandBar}
            className="hidden sm:flex items-center gap-3 flex-1 max-w-md px-4 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-teal-400 focus:border-teal-400 focus:ring-2 focus:ring-teal-100 outline-none transition text-left group"
            data-testid="command-bar-trigger"
          >
            <Search className="w-4 h-4 text-slate-400 group-hover:text-teal-600" />
            <span className="flex-1 text-slate-400 group-hover:text-slate-500">Search jobs, contractors, parks...</span>
            <div className="flex items-center gap-1 text-xs text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 bg-white">
              <Command className="w-3 h-3" />
              <span>K</span>
            </div>
          </button>
        </div>

        {/* Center: Title (mobile) */}
        <div className="lg:hidden flex items-center gap-2">
          <span className="font-semibold text-slate-800">PHS Command</span>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 lg:gap-3">
          {/* Connection Status */}
          <div 
            className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
              isConnected 
                ? 'bg-green-50 text-green-700' 
                : 'bg-amber-50 text-amber-700'
            }`}
            data-testid="connection-status"
          >
            {isConnected ? (
              <>
                <Wifi className="w-3 h-3" />
                <span>Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-3 h-3" />
                <span>Reconnecting</span>
              </>
            )}
          </div>

          {/* Quick Add */}
          <button
            onClick={() => navigate('/phs/helpdesk?action=new-job')}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-white text-sm font-medium transition hover:opacity-90"
            style={{ backgroundColor: TOKENS.colors.primaryTeal }}
            data-testid="quick-add-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Job</span>
          </button>

          {/* Notifications Bell - Enhanced */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-lg hover:bg-slate-100 transition"
              data-testid="notification-bell"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {unreadCount > 0 && (
                <span 
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white animate-pulse"
                  style={{ backgroundColor: TOKENS.colors.red }}
                  data-testid="notification-badge"
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown - Enhanced */}
            {showNotifications && (
              <div 
                className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden z-50"
                data-testid="notification-dropdown"
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-slate-600" />
                    <h3 className="font-semibold text-slate-800">Notifications</h3>
                    {unreadCount > 0 && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-red-500 text-white rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-teal-600 hover:text-teal-700 font-medium"
                      >
                        Mark all read
                      </button>
                    )}
                    <button
                      onClick={() => setShowNotifications(false)}
                      className="p-1 rounded hover:bg-slate-200 transition"
                    >
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                </div>

                {/* Notifications List */}
                <div className="max-h-[50vh] overflow-y-auto">
                  {notifications.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {notifications.map((notif) => {
                        const config = CATEGORY_CONFIG[notif.category] || CATEGORY_CONFIG.system;
                        const Icon = config.icon;
                        
                        return (
                          <div
                            key={notif.id}
                            onClick={() => handleNotificationClick(notif)}
                            className={`
                              p-3 hover:bg-slate-50 cursor-pointer transition
                              ${!notif.read ? config.bg : ''}
                              ${PRIORITY_STYLES[notif.priority] || ''}
                            `}
                            data-testid={`notification-item-${notif.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${config.bg} flex-shrink-0`}>
                                <Icon className={`w-4 h-4 ${config.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <h4 className={`text-sm font-medium truncate ${notif.read ? 'text-slate-600' : 'text-slate-900'}`}>
                                    {notif.title}
                                  </h4>
                                  {!notif.read && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        markAsRead(notif.id);
                                      }}
                                      className="p-1 rounded hover:bg-slate-200 transition flex-shrink-0"
                                      title="Mark as read"
                                    >
                                      <Check className="w-3 h-3 text-slate-400" />
                                    </button>
                                  )}
                                </div>
                                <p className={`text-xs mt-1 line-clamp-2 ${notif.read ? 'text-slate-400' : 'text-slate-600'}`}>
                                  {notif.message}
                                </p>
                                <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                                  <Clock className="w-3 h-3" />
                                  <span>{formatRelativeTime(notif.created_at)}</span>
                                  {notif.job_reference && (
                                    <>
                                      <span>•</span>
                                      <span className="font-medium text-teal-600">{notif.job_reference}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <Bell className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                      <p className="text-sm text-slate-500">No notifications yet</p>
                      <p className="text-xs text-slate-400 mt-1">You're all caught up!</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                  <div className="p-3 border-t border-slate-100 bg-slate-50">
                    <button 
                      onClick={() => {
                        setShowNotifications(false);
                        navigate('/phs/helpdesk?view=notifications');
                      }}
                      className="w-full text-center text-sm text-teal-600 hover:text-teal-700 font-medium"
                    >
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-1.5 pr-3 rounded-xl hover:bg-slate-100 transition"
              data-testid="user-menu-btn"
            >
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                style={{ backgroundColor: TOKENS.colors.darkTeal }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <span className="hidden md:block text-sm font-medium text-slate-700 max-w-[120px] truncate">
                {user?.name || 'User'}
              </span>
              <ChevronDown className="hidden md:block w-4 h-4 text-slate-400" />
            </button>

            {/* User Dropdown */}
            {showUserMenu && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-200 py-2 z-50">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="font-semibold text-slate-800">{user?.name || 'User'}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => navigate('/phs/helpdesk/governance')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 transition"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={onLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
